import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, Navigate } from "react-router-dom";

import { useLanguage } from "@/context/LanguageContext";
import { WELCOME } from "@/constants/testIds";

const FIRSTNAME_KEY = "arcana.firstName.v1";

const WelcomePage = () => {
  const { t, language } = useLanguage();
  const [firstName, setFirstName] = useState(null); // null = still loading

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setFirstName(window.localStorage.getItem(FIRSTNAME_KEY) || "");
  }, []);

  if (firstName === null) {
    return (
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
        <div className="pulse-square" />
      </div>
    );
  }
  if (!firstName) {
    return <Navigate to="/" replace />;
  }

  return (
    <main
      data-testid={WELCOME.page}
      className="relative min-h-[calc(100vh-72px)] max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14"
    >
      {/* Welcome greeting */}
      <section className="mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="rune text-sm tracking-[0.5em] uppercase text-gold/70 mb-4">
            ✦ ARCANA MYSTIQUE ✦
          </div>
          <h1
            data-testid={WELCOME.greeting}
            className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ivory tracking-tight"
          >
            <span className="block">{t.auth.welcomePageTitle}</span>
            <span className="block italic shimmer-gold">{firstName}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-ivory font-sans font-bold leading-relaxed text-base md:text-lg">
            {t.auth.welcomePageIntro}
          </p>
        </motion.div>
      </section>

      <div className="gold-hairline mb-10" />

      {/* Ritual — the 4 steps */}
      <section className="mb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <div className="md:col-span-6">
            <div className="text-sm md:text-base tracking-[0.4em] uppercase text-[#14103A] font-bold mb-3">
              ✧ {t.home.ritual}
            </div>
            <h2 className="font-serif font-light italic text-3xl md:text-4xl text-ivory leading-tight">
              {language === "fr" ? "Quatre gestes, une histoire." : "Four gestures, one story."}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {t.home.steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 * idx }}
              className="relative overflow-hidden rounded-md border border-gold/25 bg-oxblood-surface/70 p-6 md:p-7"
            >
              <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full border border-gold/12 opacity-60" />
              <div className="text-lg md:text-xl tracking-[0.35em] text-[#14103A] font-bold mb-2">
                · {String(idx + 1).padStart(2, "0")} ·
              </div>
              <h3 className="font-serif font-light text-2xl md:text-3xl text-ivory leading-tight">
                {step.t}
              </h3>
              <p className="mt-2 text-ivory/70 font-sans font-light leading-relaxed">
                {step.d}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="gold-hairline mb-10" />

      {/* Begin reading CTA */}
      <section className="text-center mb-10">
        <Link
          to="/reading"
          data-testid={WELCOME.startBtn}
          className="group inline-flex items-center gap-3 px-12 py-6 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 font-sans text-[0.75rem] tracking-[0.4em] uppercase font-semibold"
        >
          {t.auth.welcomeStartCta}
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </Link>
      </section>

      <div className="mt-10 pt-6 border-t border-gold/10 text-center">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ivory/40">{t.footer}</p>
      </div>
    </main>
  );
};

export default WelcomePage;
